import { Button, Icon, SplitColumnsContainer } from '@startlibs/components';
import {TextInput} from '@startlibs/form'
import {callIfFunction, getColor, media, parseDate} from '@startlibs/utils';
import React, { useEffect } from 'react';
import _ from 'lodash/fp'
import styled, { css } from 'styled-components';
import {
  BIOPSY_FACILITIES,
  HOSPITALS,
  PHYSICIAN_OR_PROVIDER,
  SCAN_FACILITIES,
  OTHER_FACILITIES
} from '../enums/MedicalRecordLocationType'
import { CASE_REVIEWED, WAITING_ACCEPTANCE } from '../enums/CaseState';
import {ConfirmDialog, useConfirmDialog} from '../hooks/useConfirmDialog'
import { FormBox } from '../components/FormBox';
import {
  ReleaseDetails,
  ReleaseLocationCard,
  ReleaseLocationContent
} from '../request/medicalRelease/ReleaseLocationCard'
import {getJwt} from '../hooks/useJwt'
import {isStateBefore} from '../request/utils'
import { jwtFormFetcher } from '../utils/authFetch';
import {FormattedMessage, useIntl} from 'react-intl';
import {buildValidation, intlRequired} from '../utils/validation';
import StyledTelInput from '../components/StyledTelInput';
import { darken, desaturate, transparentize } from 'polished';
import { lazyProviderInfo } from '../components/WithProvider';
import {getLocaleCountry} from "../utils/getInitLocale";


export const ReleaseList = ({caseRequest,isPatient,editItemButton,onEmpty,suspendedButton,children,releases,setReleases}) => {


  useEffect(() => {
    if (!releases.length) {
      callIfFunction(onEmpty)
    }
  },[])

  const providerInfo = lazyProviderInfo.read()
  const isMultiLanguage = providerInfo?.preferredLanguage?.options && providerInfo?.preferredLanguage?.options?.length > 1
  const languages = providerInfo?.preferredLanguage?.options


  const setRelease = (id) => (update) => setReleases(_.map(release => release.id === id ? callIfFunction(update,release) : release))

  const physicianOrProviders = releases.filter(_.matchesProperty('medicalRecordLocationItem.locationType', PHYSICIAN_OR_PROVIDER))
  const hospitals = releases.filter(_.matchesProperty('medicalRecordLocationItem.locationType', HOSPITALS))
  const scanFacilities = releases.filter(_.matchesProperty('medicalRecordLocationItem.locationType', SCAN_FACILITIES))
  const biopsyFacilities = releases.filter(_.matchesProperty('medicalRecordLocationItem.locationType', BIOPSY_FACILITIES))
  const otherFacilities = releases.filter(_.matchesProperty('medicalRecordLocationItem.locationType', OTHER_FACILITIES))

  return <>
    {physicianOrProviders.length > 0 && <>
      <h4><FormattedMessage
        defaultMessage="Physicians and providers seen for this condition"
        description="Medical release list, header for physicians"
      /></h4>
      {physicianOrProviders.map(release =>
        <ReleaseLocationItem
          isPatient={isPatient}
          editItemButton={editItemButton}
          suspendedButton={callIfFunction(suspendedButton,releases)}
          setRelease={setRelease(release.id)}
          release={release}
          caseRequest={caseRequest}
          isMultiLanguage={isMultiLanguage}
          languages={languages}
        />)}
      </>
    }
    {hospitals.length > 0 && <>
      <h4><FormattedMessage
        defaultMessage="Hospitals visited for this condition"
        description="Medical release list, header for hospitals"
      /></h4>
      {hospitals.map(release =>
        <ReleaseLocationItem
          isPatient={isPatient}
          editItemButton={editItemButton}
          suspendedButton={callIfFunction(suspendedButton,releases)}
          release={release}
          setRelease={setRelease(release.id)}
          caseRequest={caseRequest}
          isMultiLanguage={isMultiLanguage}
          languages={languages}
        />)}
    </>}
    {scanFacilities.length > 0 && <>
      <h4><FormattedMessage
        defaultMessage="Facilities visited for scans, i.e MRI and CT scan"
        description="Medical release list, header for scan facilities"
      /></h4>
      {scanFacilities.map(release =>
        <ReleaseLocationItem
          isPatient={isPatient}
          editItemButton={editItemButton}
          suspendedButton={callIfFunction(suspendedButton,releases)}
          release={release}
          caseRequest={caseRequest}
          setRelease={setRelease(release.id)}
          isMultiLanguage={isMultiLanguage}
          languages={languages}
        />)}
    </>}
    {biopsyFacilities.length > 0 && <>
      <h4><FormattedMessage
        defaultMessage="Facilities visited for biopsy"
        description="Medical release list, header for biopsy facilities"
      /></h4>
      {biopsyFacilities.map(release =>
        <ReleaseLocationItem
          isPatient={isPatient}
          editItemButton={editItemButton}
          suspendedButton={callIfFunction(suspendedButton,releases)}
          release={release}
          caseRequest={caseRequest}
          setRelease={setRelease(release.id)}
          isMultiLanguage={isMultiLanguage}
          languages={languages}
        />)}
    </>}
    {otherFacilities.length > 0 && <>
      <h4><FormattedMessage
        defaultMessage="Other Facilities"
        description="Medical release list, header for other facilities"
      /></h4>
      {otherFacilities.map(release =>
        <ReleaseLocationItem
          isPatient={isPatient}
          editItemButton={editItemButton}
          suspendedButton={callIfFunction(suspendedButton,releases)}
          release={release}
          caseRequest={caseRequest}
          setRelease={setRelease(release.id)}
          isMultiLanguage={isMultiLanguage}
          languages={languages}
        />)}
    </>}
    {callIfFunction(children,releases)}
  </>
}


const ReleaseLocationItem = ({release,setRelease,caseRequest,editItemButton,isPatient,suspendedButton, isMultiLanguage=false,languages=[]}) => {

  const intl = useIntl()
  const date = new Date()
  date.setHours(0)

  const expiredDate = parseDate(release.medicalRecordLocationItem.dateExpiration,date,'MM-dd-yyyy').getTime() < date.getTime()
  const suspendedByContact = release.suspend
  const lockedByStatus = !isStateBefore(caseRequest.state,WAITING_ACCEPTANCE) && caseRequest.state !== CASE_REVIEWED
  const externalAccessBlocked = caseRequest?.caseInfo?.externalAccessBlocked === true
  
  const locked = suspendedByContact || expiredDate || lockedByStatus || externalAccessBlocked

  const preValidation = buildValidation({name: [intlRequired(intl)], city: [intlRequired(intl)]})
  const edit = useConfirmDialog(<ConfirmDialog
      title={intl.formatMessage({
        defaultMessage: "Editing medical location contact",
        description: "Medical record location form, edit contact dialog title"
      })}
    closeLabel={intl.formatMessage({
      defaultMessage:"Cancel",
      description: "Cancel button"
    })}
      action={jwtFormFetcher(getJwt())("/api/medicalRecordLocationFormContact?requestId="+caseRequest.requestId,{method:"PUT"})}
      confirm={<Button highlight><FormattedMessage
        defaultMessage="Save changes"
        description="Save changes button"
      /></Button>}
      values={{...release.medicalRecordLocationItem,locationFormId:release.id}}
      onSuccess={(values) => setRelease(_.set('medicalRecordLocationItem',values))}
      notify={intl.formatMessage({
        defaultMessage: "Provider contact details updated successully.",
        description: "Medical record location form, contact details updated success message"
      })}
      preValidation={preValidation}
    >
      <p><FormattedMessage
        defaultMessage="You are editing the following medical record location. Please review the details and make the desired changes in the inputs below:"
        description="Medical record location form, edit contact dialog description"
      /></p>
      <ReleaseLocationCard location={release.medicalRecordLocationItem}/>
      <FormBox>
        <TextInput
          label={intl.formatMessage({
            defaultMessage: "Facility",
            description: "Medical record location form, facility input label"
          })}
          path="name"
          mandatory
          disabled={locked || release.medicalRecordsReleaseLicense.signature || isPatient}
        />
        <TextInput
          label={intl.formatMessage({
            defaultMessage: "City/State/Province",
            description: "Medical record location form, city/state input label"
          })}
          path="city"
          mandatory
          disabled={(locked || release.medicalRecordsReleaseLicense.signature || isPatient) 
            && (release.medicalRecordLocationItem.city != null && release.medicalRecordLocationItem.city != "")}
        />
        <TextInput
          label={intl.formatMessage({
            defaultMessage: "New contact name",
            description: "Medical record location form, new contact name input label"
          })}
          path="contactName"
          helpText={intl.formatMessage({
            defaultMessage: "(Optional)",
            description: "Optional input"
          })}
        />
        <SplitColumnsContainer>
          {/* <TextInput
            label="New phone/fax"
            path="phoneNumber"
            helpText="(Optional)"
          /> */}
          <StyledTelInput
            label={intl.formatMessage({
              defaultMessage: "New phone/fax",
              description: "Medical record location form, new phone/fax input label"
            })}
            path="phoneNumber"
            helpText={intl.formatMessage({
              defaultMessage: "(Optional)",
              description: "Optional input"
            })}
            initOptions={{
              initialCountry: getLocaleCountry(intl.locale),
              separateDialCode: false,
              autoPlaceholder: "aggressive",
              formatOnDisplay: true,
              placeholderNumberType: "FIXED_LINE_OR_MOBILE",
              nationalMode: false,
              countryOrder: [intl.formatMessage({
                defaultMessage: "us",
                description: "Default country code for phone number"
              })]
            }}
            tabIndex={1}
          />
          <TextInput
            label={intl.formatMessage({
              defaultMessage: "New email",
              description: "Medical record location form, new email input label"
            })}
            path="email"
            helpText={intl.formatMessage({
              defaultMessage: "(Optional)",
              description: "Optional input"
            })}
          />
        </SplitColumnsContainer>
      </FormBox>
    </ConfirmDialog>
  )

  return <ReleaseLocationReadOnly suspended={expiredDate || suspendedByContact} lockedByStatus={lockedByStatus}>
    <ReleaseLocationContent location={release.medicalRecordLocationItem} editContacts={!locked && edit}/>
    <MainActionContainer>
    {locked ?
      <SuspendedLabel outline small>
        {
          expiredDate
            ? intl.formatMessage({
              defaultMessage: "Expired authorization",
              description: "Medical release list, authorization label",
            })
            : suspendedByContact
            // ? "Suspended by case contact"
            ? intl.formatMessage({
                defaultMessage:`Suspended by patient contact`,
              description: "Medical release list, suspended by contact label"
            })
            : (isPatient
                ? intl.formatMessage({
                  defaultMessage: "Editing locked",
                  description: "Medical release list, editing locked label"
                })
                : intl.formatMessage({
                  defaultMessage: "Requesting records locked",
                  description: "Medical release list, requesting records locked label"
                })
              )
        }</SuspendedLabel>
      :
      callIfFunction(editItemButton,release,setRelease)
    }
    </MainActionContainer>
    <ReleaseDetails isPatient={isPatient} isExpired={expiredDate || suspendedByContact} release={release} suspendedButton={suspendedButton}
      isMultiLanguage={isMultiLanguage} languages={languages}
    />
  </ReleaseLocationReadOnly>
}

const ReleaseLocationReadOnly = styled.div `
  padding: 1rem 1.25rem;
  background: ${props => transparentize(0.93, darken(0.012, desaturate(0.4, getColor('main')(props))))};
  box-shadow: 0 0 0 1px ${props => transparentize(0.7, darken(0.012, desaturate(0.4, getColor('main')(props))))};
  border-radius: 5px;
  margin-bottom: 2rem;
  position: relative;
  & + & {
    margin-top: -1rem;
  }
  :last-child {
    margin-bottom: 0;
  }
  ${props => props.suspended && css`
    background: ${getColor('gray240')};
    box-shadow: 0 0 0 1px ${getColor('gray210')};
  `}
  .DocumentLink {
    text-decoration: none;
    color: ${getColor('main')};
    span {
      text-decoration: underline;
    }
    ${Icon} {
      vertical-align: text-top;
      font-size: 17px;
      text-decoration: none;
      margin-right: 4px;
    }
  }
`
const MainActionContainer = styled.div`
  ${media.mobile`
    margin-top: 1rem;
  `}
  ${media.desktop`
    position: absolute;
    right: 1rem;
    top: 1rem;
  `}
`

const SuspendedLabel = styled(Button) `
  box-shadow: none;
  pointer-events: none;
  opacity: 0.75;
`
